import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { TrashIcon } from "@heroicons/react/24/outline";

import {
  JobStatus,
  useJobQuery,
  useUpdateJobMutation,
} from "../../graphql/generated/graphql";
import {
  cityStates,
  GradeConditionLabels,
  genderOptions,
  jobFieldTypes,
  jobStatusTypes,
  militaryStatusTypes,
} from "../../options";
import LoadingButton from "../Button/LoadingButton";
import CardModal from "../Modal/CardModal";

interface JobDetailsProps {
  jobId: number;
}

const JobDetails: React.FC<JobDetailsProps> = ({ jobId }) => {
  const { data, loading, error } = useJobQuery({
    variables: { id: jobId },
  });
  const [updateJob, { loading: mutationLoading }] = useUpdateJobMutation();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const navigate = useNavigate();
  const handleArchiveJob = () => {
    if (job?.status !== JobStatus.Published) {
      return;
    }
    setShowConfirmModal(true);
  };

  const confirmArchiveJob = () => {
    updateJob({
      variables: {
        input: {
          id: jobId,
          patch: {
            status: JobStatus.Archived,
          },
        },
      },
    })
      .then((response) => {
        toast.success("حذف شد");
        setShowConfirmModal(false);
        navigate("/jobs");
      })
      .catch((err) => {
        console.error("Error updating job status", err);
        toast.error(".خطا هنگام حذف آگهی! لطفا مجدد تلاش کنید");
      });
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-28 w-28 border-t-2 border-b-2 border-slate-500"></div>
      </div>
    );
  if (error) return <p>{error.message}</p>;

  const job = data?.job;
  if (!job) return <p>اطلاعات وجود ندارد </p>;

  return (
    <div className="">
      {job.translations.nodes[0]?.title && (
        <div className="border-b p-2 mb-4 text-right">
          <p className="my-1 text-lg">عنوان:</p>{" "}
          <h1 className="text-lg font-medium">
            {job.translations.nodes[0]?.title || ""}
          </h1>
        </div>
      )}{" "}
      <div className="flex-col items-center text-right px-2 bg-gray-100 rounded-lg shadow-lg">
        {job.translations.nodes[0]?.description && (
          <div className="border-b p-2 mb-4">
            <p className="my-1 text-lg">توضیحات:</p>{" "}
            <p className="text-sm text-gray2">
              {job.translations.nodes[0]?.description || ""}
            </p>
          </div>
        )}
        {job.status && (
          <div className="border-b p-2 mb-4 flex justify-between">
            <div>
              <h3 className="my-1 text-lg">وضعیت آگهی:</h3>
              <p className="text-sm mt-1 text-gray2">
                {jobStatusTypes[job.status] || "نامشخص"}
              </p>
            </div>
          </div>
        )}
        <div className="border-b p-2 mb-4 justify-between">
          <h3 className="my-1 text-lg"> تحصیلات:</h3>
          <div className="flex gap-2">
            <p className="my-1 text-sm">مقطع تحصیلی:</p>
            <p className="text-sm mt-1 text-gray2">
              {job.gradeConditions
                ?.map((grade) => grade && GradeConditionLabels[grade])
                .join("، ") || "نامشخص"}
            </p>
          </div>

          <div className="flex gap-2">
            <h3 className="my-1 text-sm">رشته :</h3>
            <p className="text-sm mt-1 text-gray2">
              {job.translations
                ? job.translations.nodes[0].fieldOfStudy
                : "نامشخص"}
            </p>
          </div>
          {job?.translations?.nodes[0]?.orientation && (
            <div className="flex gap-2">
              <p className="mt-1 text-sm">گرایش:</p>
              <p className="text-sm mt-1 text-gray2">
                {job.translations?.nodes[0]?.orientation}
              </p>
            </div>
          )}
        </div>
        {job.isRemote !== undefined && (
          <div className="border-b p-2 mb-4">
            <p className="my-1 text-lg">
              دورکاری:
              <span className="text-sm text-gray2 mr-2">
                {job.isRemote ? "بله" : "خیر"}
              </span>
            </p>
          </div>
        )}
        {(job.city || job.state) && (
          <div className="border-b p-2 mb-4">
            <p className="my-1 text-lg">ادرس محل کار:</p>
            <div className="flex justify-start gap-4 mb-2">
              {/* <p className="text-base">
                کشور: <span className="text-sm text-gray2">ایران</span>
              </p>{" "} */}
              {job.state && (
                <p className="text-base">
                  استان:{" "}
                  <span className="text-sm text-gray2">
                    {" "}
                    {cityStates.find(
                      (state) => state.value === String(job.state)
                    )?.label || ""}
                  </span>
                </p>
              )}
              {job.city && (
                <p className="text-base flex justify-center items-center">
                  شهر: <span className="text-sm text-gray2"> {job.city}</span>
                </p>
              )}
            </div>
            {job.address && (
              <p className="mt-1">
                آدرس: <span className="text-sm text-gray2">{job.address}</span>
              </p>
            )}
          </div>
        )}
        {job.workExperienceCondition ? (
          <div className="border-b p-2 mb-4">
            <p className="my-1 text-lg">
              سابقه کاری:
              <span className="text-sm text-gray2 mr-2">
                {job.workExperienceCondition} سال
              </span>
            </p>
          </div>
        ) : (
          <div className="border-b p-2 mb-4">
            <p className="my-1 text-lg">سابقه کاری:</p>
            <p className="text-sm mt-1 text-gray2">بدون سابقه کاری</p>
          </div>
        )}
        {job.minAgeCondition || job.maxAgeCondition ? (
          <div className="border-b p-2 mb-4">
            <p className="my-1 text-lg">بازه سنی:</p>
            <div className="flex gap-2">
              {job.minAgeCondition && (
                <p className="my-1 text-sm text-gray2">
                  حداقل سن: {job.minAgeCondition}
                </p>
              )}
              {job.maxAgeCondition && (
                <p className="my-1 text-sm text-gray2">
                  حداکثر سن: {job.maxAgeCondition}
                </p>
              )}
            </div>
          </div>
        ) : null}
        {job.hoursPerWeek?.start?.value && job.hoursPerWeek?.end?.value && (
          <div className="border-b p-2 mb-4">
            <h3 className="my-1 text-lg">ساعت در هفته:</h3>{" "}
            <p>
              <span className="text-sm text-gray2">
                شروع: {job.hoursPerWeek.start.value},
              </span>{" "}
              <span className="text-sm text-gray2">
                پایان:
                {job.hoursPerWeek.end.value}
              </span>
            </p>
          </div>
        )}
        <div className="border-b p-2 mb-4">
          <h3 className="my-1 text-lg">وضعیت نظام وظیفه:</h3>
          {job.militaryStatus && job.militaryStatus.length > 0 ? (
            <ul className="text-sm text-gray2">
              {job.militaryStatus.map((status, index) => (
                <li key={index}>
                  {status !== null
                    ? militaryStatusTypes[status] || "نامشخص"
                    : "نامشخص"}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm mt-1 text-gray2">نامشخص</p>
          )}
        </div>
        <div className="border-b p-2 mb-4">
          <h3 className="my-1 text-lg">جنسیت‌ها:</h3>
          {job.genders && job.genders.length > 0 ? (
            <ul className="text-sm text-gray2">
              {job.genders.map((gender, index) => {
                const genderOption = genderOptions.find(
                  (option) => option.value === gender
                );
                return (
                  <li key={index} className="mt-1 text-gray2 text-sm">
                    {genderOption ? genderOption.label : "نامشخص"}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm mt-1 text-gray2">نامشخص</p>
          )}
        </div>
        {job.pipeline?.translations.nodes[0]?.title && (
          <div className="border-b p-2 mb-4">
            <h3 className="my-1 text-lg">مراحل استخدام:</h3>
            <p className="text-sm text-gray2">
              {job.pipeline.translations.nodes[0]?.title || ""}
            </p>
          </div>
        )}
        {job.fields.nodes.length > 0 && (
          <div className="border-b p-2 mb-4">
            <h3 className="my-1 text-lg">فیلدها:</h3>
            <ul className="text-sm">
              {job.fields.nodes.map((field) => (
                <li key={field.id} className="mt-1 text-gray2">
                  {jobFieldTypes[field.type]?.title || "نامشخص"}
                </li>
              ))}
            </ul>
          </div>
        )}
        {job.tags.nodes.length > 0 && (
          <div className="border-b p-2 mb-4">
            <h3 className="my-1 text-lg">برچسب‌ها:</h3>
            <ul>
              {job.tags.nodes.map((tag) => (
                <li key={tag.id} className="mt-1 text-gray2">
                  {tag.title}
                </li>
              ))}
            </ul>
          </div>
        )}
        {job.jobQuestionnaires.nodes.length > 0 && (
          <div className="border-b p-2 mb-4">
            <h3 className="mt-4 text-lg">پرسشنامه:</h3>
            {job.jobQuestionnaires.nodes.map((Node) => (
              <div key={Node.nodeId}>
                <h4 className="my-1 text-base text-gray2">
                  {Node.questionnaire?.translations.nodes[0]?.title || ""}
                </h4>
                <ul>
                  {Node.questionnaire?.questions.nodes.map((question) => (
                    <li key={question.id} className="mt-1 text-gray2">
                      {question.translations.nodes[0]?.title || ""}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {job.skills.nodes.length > 0 && (
          <div className="border-b p-2 mb-4">
            <h3 className="mt-4 text-lg">دانش و مهارت‌:</h3>
            <ul className="flex gap-x-4">
              {job.skills.nodes.map((skill) => (
                <li key={skill.title} className="mt-1 text-gray2">
                  {skill.title || ""}
                </li>
              ))}
            </ul>
          </div>
        )}
        {job.competencies.nodes.length > 0 && (
          <div className="border-b p-2 mb-4">
            <h3 className="mt-4 text-lg">صلاحیت‌:</h3>
            <ul>
              {job.competencies.nodes.map((competency) => (
                <li key={competency.id} className="mt-1 text-gray2">
                  {competency.title}
                </li>
              ))}
            </ul>
          </div>
        )}
        {showConfirmModal && (
          <CardModal
            open={showConfirmModal}
            setOpen={setShowConfirmModal}
            request={confirmArchiveJob}
            children={
              <div className="text-center flex-col justify-center items-stretch pt-1">
                <h2 className="">از حذف این آگهی اطمینان دارید؟</h2>
                <div className="flex gap-2 mt-4 justify-center items-end">
                  <button
                    className="w-24 px-4 py-1.5 bg-red-500 text-white rounded-lg"
                    onClick={confirmArchiveJob}
                  >
                    {mutationLoading ? (
                      <LoadingButton />
                    ) : job?.status === JobStatus.Published ? (
                      "حذف"
                    ) : (
                      "حذف"
                    )}{" "}
                  </button>
                  <button
                    className="w-24 px-5 py-1.5 bg-gray-500 text-white rounded-lg"
                    onClick={() => setShowConfirmModal(false)}
                  >
                    لغو
                  </button>
                </div>
              </div>
            }
          />
        )}
      </div>
      <button
        className="w-10/12 mx-auto flex justify-center gap-x-2 my-2 py-2 bg-red-500 text-white rounded-lg text-sm"
        onClick={handleArchiveJob}
        disabled={job?.status !== JobStatus.Published}
      >
        {job.status === JobStatus.Published ? (
          <>
            حذف آگهی
            <TrashIcon className="h-5 w-5 text-white" />
          </>
        ) : (
          <>این آگهی حذف شده است !</>
        )}
      </button>
    </div>
  );
};

export default JobDetails;
